import React, { useState, useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthProvider';
import { LoadingScreen } from '@/components/ui/Loading/LoadingScreen';
import { SubscriptionGate } from './SubscriptionGate';
import { type PlanId } from '@/constants/planLimits';
import designTokens from '@/design-tokens';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'owner' | 'admin' | 'supervisor' | 'production_worker';
  requiredFeature?: string;
  requiredPlan?: PlanId;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  requiredFeature,
  requiredPlan
}) => {
  const { user, userProfile, tenant, loading, reloadUserData } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [waitTime, setWaitTime] = useState(0);
  const [hasRetried, setHasRetried] = useState(false);

  // Effect to track wait time for profile/tenant loading - reduced from 5s to 3s
  useEffect(() => {
    if (user && (!userProfile || !tenant) && !loading && waitTime < 3) {
      const timer = setTimeout(() => {
        setWaitTime(prev => prev + 1);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [user, userProfile, tenant, loading, waitTime]);

  // Reset wait time when user changes
  useEffect(() => {
    setWaitTime(0);
    setHasRetried(false);
  }, [user?.id]);

  if (loading) {
    return <LoadingScreen title="Loading" message="Initializing your workspace..." />;
  }

  if (!user) {
    // Redirect to sign in with return URL
    return <Navigate to="/auth/signin" state={{ from: location }} replace />;
  }

  // Handle profile/tenant loading more gracefully due to database timeout issues
  if (!userProfile || !tenant) {
    // Check if we're already in the auth flow
    if (location.pathname.startsWith('/auth/')) {
      // Don't redirect if already in auth flow
      return <>{children}</>;
    }
    
    // For dashboard access, be more permissive due to database issues
    if (location.pathname.startsWith('/dashboard') || location.pathname === '/') {
      // Wait a shorter time for data to load
      if (user && waitTime < 2) {
        return (
          <LoadingScreen 
            title="Loading Your Workspace" 
            message="Getting your account details ready..." 
          />
        );
      }
      
      // After 2 seconds, allow access with degraded functionality
      console.log('Database timeout detected, allowing dashboard access with minimal profile');
      // Pass through to dashboard - components will handle missing data gracefully
      return <>{children}</>;
    }
    
    // For non-dashboard routes, still redirect to setup after waiting
    if (user && waitTime >= 3) {
      console.log('No profile/tenant after waiting, redirecting to signup flow');
      return <Navigate to="/auth/payment" replace />;
    }
    
    // Default loading state for other routes
    return (
      <LoadingScreen 
        title="Loading" 
        message="Initializing your workspace..." 
      />
    );
  }

  // Check role-based access
  if (requiredRole) {
    const roleHierarchy: Record<string, number> = {
      'owner': 4,
      'admin': 3,
      'supervisor': 2,
      'production_worker': 1
    };

    const userRoleLevel = roleHierarchy[userProfile.role] || 0;
    const requiredRoleLevel = roleHierarchy[requiredRole] || 0;

    if (userRoleLevel < requiredRoleLevel) {
      // Redirect to home which triggers DefaultRedirect (role-appropriate landing page)
      return <Navigate to="/" replace />;
      /* Original permission-denied page (kept for reference):
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: designTokens.surface?.greyscale?.fog || '#f9fafb',
          padding: '2rem'
        }}>
          <div style={{
            textAlign: 'center',
            backgroundColor: 'white',
            borderRadius: designTokens.radius?.lg || '0.75rem',
            boxShadow: 'none',
            padding: '2rem',
            maxWidth: '400px',
            width: '100%'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              margin: '0 auto 1.5rem',
              borderRadius: '50%',
              backgroundColor: '#fef2f2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5">
                <path d="M12 15V9m-8 4a8 8 0 0016 0 8 8 0 00-16 0zm4 0h8" />
              </svg>
            </div>
            <h2 style={{
              fontSize: designTokens.font?.size?.xl || '1.25rem',
              fontWeight: designTokens.font?.weight?.semibold || '600',
              marginBottom: '0.5rem',
              color: designTokens.text?.primary || '#111827'
            }}>
              Access Denied
            </h2>
            <p style={{
              fontSize: designTokens.font?.size?.base || '1rem',
              color: designTokens.text?.secondary || '#6b7280',
              marginBottom: '1.5rem'
            }}>
              You don't have permission to access this page.
            </p>
            <button
              onClick={() => window.history.back()}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: designTokens.surface?.brand?.primary || '#2bb673',
                color: 'white',
                border: 'none',
                borderRadius: designTokens['button/radius'] || '1.5rem',
                fontSize: designTokens.font?.size?.base || '1rem',
                fontWeight: designTokens.font?.weight?.medium || '500',
                cursor: 'pointer'
              }}
            >
              Go Back
            </button>
          </div>
        </div>
      );
      */
    }
  }

  // Check plan-tier access (e.g., transport-orders requires Growth+)
  if (requiredPlan && tenant) {
    const TIER_ORDER: Record<string, number> = { starter: 0, growth: 1, pro: 2 };
    const currentTier = TIER_ORDER[tenant.subscription_plan] ?? 0;
    const requiredTier = TIER_ORDER[requiredPlan] ?? 0;
    if (currentTier < requiredTier) {
      const planName = requiredPlan.charAt(0).toUpperCase() + requiredPlan.slice(1);
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: designTokens.surface?.greyscale?.fog || '#f9fafb',
          padding: '2rem'
        }}>
          <div style={{
            textAlign: 'center',
            backgroundColor: 'white',
            borderRadius: designTokens.radius?.lg || '0.75rem',
            boxShadow: 'none',
            padding: '2rem',
            maxWidth: '400px',
            width: '100%'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              margin: '0 auto 1.5rem',
              borderRadius: '50%',
              backgroundColor: '#dbeafe',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <h2 style={{
              fontSize: designTokens.font?.size?.xl || '1.25rem',
              fontWeight: designTokens.font?.weight?.semibold || '600',
              marginBottom: '0.5rem',
              color: designTokens.text?.primary || '#111827'
            }}>
              {planName} Feature
            </h2>
            <p style={{
              fontSize: designTokens.font?.size?.base || '1rem',
              color: designTokens.text?.secondary || '#6b7280',
              marginBottom: '1.5rem'
            }}>
              This feature is available on the {planName} plan and above. Upgrade to get access.
            </p>
            <button
              onClick={() => navigate('/settings/subscription')}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: designTokens.surface?.brand?.primary || '#2bb673',
                color: 'white',
                border: 'none',
                borderRadius: designTokens['button/radius'] || '1.5rem',
                fontSize: designTokens.font?.size?.base || '1rem',
                fontWeight: designTokens.font?.weight?.medium || '500',
                cursor: 'pointer'
              }}
            >
              Upgrade Plan
            </button>
          </div>
        </div>
      );
    }
  }

  // Check feature-based access
  if (requiredFeature) {
    const limits = tenant.plan_limits || {};
    const usage = tenant.current_usage || {};
    
    if (limits[requiredFeature] !== undefined && usage[requiredFeature] !== undefined) {
      if (usage[requiredFeature] >= limits[requiredFeature]) {
        return (
          <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: designTokens.surface?.greyscale?.fog || '#f9fafb',
            padding: '2rem'
          }}>
            <div style={{
              textAlign: 'center',
              backgroundColor: 'white',
              borderRadius: designTokens.radius?.lg || '0.75rem',
              boxShadow: 'none',
              padding: '2rem',
              maxWidth: '400px',
              width: '100%'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                margin: '0 auto 1.5rem',
                borderRadius: '50%',
                backgroundColor: '#fef9c3',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#eab308" strokeWidth="2.5">
                  <path d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
              </div>
              <h2 style={{
                fontSize: designTokens.font?.size?.xl || '1.25rem',
                fontWeight: designTokens.font?.weight?.semibold || '600',
                marginBottom: '0.5rem',
                color: designTokens.text?.primary || '#111827'
              }}>
                Limit Reached
              </h2>
              <p style={{
                fontSize: designTokens.font?.size?.base || '1rem',
                color: designTokens.text?.secondary || '#6b7280',
                marginBottom: '1rem'
              }}>
                You've reached the limit for {requiredFeature} on your current plan.
              </p>
              <p style={{
                fontSize: designTokens.font?.size?.sm || '0.875rem',
                color: designTokens.text?.tertiary || '#9ca3af',
                marginBottom: '1.5rem'
              }}>
                Using {usage[requiredFeature]} of {limits[requiredFeature]}
              </p>
              <button
                onClick={() => navigate('/settings/subscription')}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: designTokens.surface?.brand?.primary || '#2bb673',
                  color: 'white',
                  border: 'none',
                  borderRadius: designTokens['button/radius'] || '1.5rem',
                  fontSize: designTokens.font?.size?.base || '1rem',
                  fontWeight: designTokens.font?.weight?.medium || '500',
                  cursor: 'pointer'
                }}
              >
                Upgrade Plan
              </button>
            </div>
          </div>
        );
      }
    }
  }

  // Check if trial has expired or subscription is inactive
  // Allow owners through to /settings/subscription so they can actually pay
  const isSubscriptionPage = location.pathname === '/settings/subscription';
  const isTrialExpired = tenant.subscription_status === 'trial' && tenant.trial_ends_at && new Date(tenant.trial_ends_at) < new Date();

  if (!isSubscriptionPage || !userProfile || userProfile.role !== 'owner') {
    if (isTrialExpired || tenant.subscription_status === 'trial_expired') {
      return <SubscriptionGate type="trial_expired" />;
    }

    if (tenant.subscription_status === 'cancelled' || tenant.subscription_status === 'past_due') {
      return <SubscriptionGate type={tenant.subscription_status === 'cancelled' ? 'cancelled' : 'past_due'} />;
    }
  }

  return <>{children}</>;
};